// PREZENCE — Profile Page
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [form, setForm]     = useState({ full_name: user?.full_name || '', phone: user?.phone || '' });
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);

  const getInitials = n => (n||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { full_name: form.full_name, phone: form.phone });
      updateUser({ full_name: form.full_name, phone: form.phone });
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New passwords do not match'); return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters'); return;
    }
    setChangingPw(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed successfully!');
      setPwForm({ current_password:'', new_password:'', confirm_password:'' });
      setShowPwForm(false);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to change password'); }
    finally { setChangingPw(false); }
  };

  const INFO_ITEMS = [
    { label:'Email',       value: user?.email },
    { label:'Role',        value: user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1) },
    { label:'Department',  value: user?.department || '—' },
    { label:'Designation', value: user?.designation || '—' },
    { label:'Employee ID', value: user?.employee_id || '—' },
    { label:'Shift',       value: user?.shift_start && user?.shift_end ? `${user.shift_start?.slice(0,5)} – ${user.shift_end?.slice(0,5)}` : '—' },
    { label:'Type',        value: user?.is_remote ? 'Remote' : 'In-office' },
    { label:'Company ID',  value: user?.company_id?.slice(0,8) + '...' },
  ];

  return (
    <div style={{ animation:'fadeIn 0.4s ease', maxWidth:700, margin:'0 auto' }}>
      <div className="page-header">
        <h1 className="page-title">My Profile</h1>
        <p className="page-sub">View and update your personal information</p>
      </div>

      {/* Profile card */}
      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:20, marginBottom:28, paddingBottom:24, borderBottom:'1px solid rgba(56,189,248,0.08)' }}>
          <div className="avatar avatar-lg" style={{ width:64, height:64, fontSize:22 }}>{getInitials(user?.full_name)}</div>
          <div>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, color:'#f0f6ff', marginBottom:4 }}>{user?.full_name}</h2>
            <p style={{ fontSize:13, color:'#64748b' }}>{user?.designation} · {user?.department}</p>
            <span className={`badge ${user?.role==='admin'||user?.role==='superadmin' ? 'badge-blue' : user?.role==='hr' ? 'badge-purple' : 'badge-green'}`} style={{ marginTop:6 }}>
              {user?.role?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Info grid */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, marginBottom:28 }}>
          {INFO_ITEMS.map((item,i) => (
            <div key={i} style={{ padding:'12px 14px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.05)', borderRadius:10 }}>
              <div style={{ fontSize:10, color:'#334155', fontFamily:"'JetBrains Mono'", letterSpacing:'0.1em', marginBottom:5 }}>{item.label.toUpperCase()}</div>
              <div style={{ fontSize:13, fontWeight:600, color:'#94a3b8' }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Edit form */}
        <form onSubmit={handleProfileSave}>
          <div style={{ fontSize:11, color:'#475569', fontFamily:"'JetBrains Mono'", letterSpacing:'0.1em', marginBottom:14 }}>EDITABLE INFORMATION</div>
          <div className="grid-2" style={{ marginBottom:16 }}>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">FULL NAME</label>
              <input className="form-input" value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))} />
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">PHONE</label>
              <input className="form-input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+91 9876543210" />
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
            {saving ? <><div className="spinner" style={{width:13,height:13}} /> Saving...</> : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Password */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">CHANGE PASSWORD</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowPwForm(s=>!s)}>
            {showPwForm ? 'Cancel' : 'Change Password'}
          </button>
        </div>
        {showPwForm && (
          <form onSubmit={handlePasswordChange}>
            <div className="form-group">
              <label className="form-label">CURRENT PASSWORD</label>
              <input className="form-input" type="password" value={pwForm.current_password} onChange={e=>setPwForm(f=>({...f,current_password:e.target.value}))} required />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">NEW PASSWORD</label>
                <input className="form-input" type="password" value={pwForm.new_password} onChange={e=>setPwForm(f=>({...f,new_password:e.target.value}))} minLength={8} required />
              </div>
              <div className="form-group">
                <label className="form-label">CONFIRM PASSWORD</label>
                <input className="form-input" type="password" value={pwForm.confirm_password} onChange={e=>setPwForm(f=>({...f,confirm_password:e.target.value}))} required />
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={changingPw}>
              {changingPw ? <><div className="spinner" style={{width:13,height:13}} /> Changing...</> : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
